import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight, ChevronLeft, User, Building, Camera, Music, Heart, Zap, Dumbbell, Utensils, Coffee, Palette, Car, Mic, FileText, Instagram, Mail, BarChart3, Store, ShoppingBag } from 'lucide-react';

// Client personas with alternating persona cards and image cards
const clients = [
  {
    name: "Shopify Merchants",
    type: "E-commerce Stores",
    category: "E-commerce",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=400&fit=crop",
    logo: "SHOPIFY MERCHANTS",
    icon: <ShoppingBag />
  },
  {
    name: "Independent Experts",
    type: "Solo Professionals",
    category: "Professional Services",
    image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop",
    logo: "INDEPENDENT EXPERTS",
    icon: <User />
  },
  {
    name: "Fitness Trainers",
    type: "Personal Trainers",
    category: "Health & Fitness",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=400&fit=crop&crop=face",
    logo: "FITNESS TRAINERS",
    icon: <Dumbbell />
  },
  {
    name: "Wellness Coaches",
    type: "Health & Wellness",
    category: "Wellness Services",
    image: "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=400&h=400&fit=crop",
    logo: "WELLNESS COACHES",
    icon: <Heart />
  },
  {
    name: "Small Agencies",
    type: "Creative Studios",
    category: "Creative Services",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
    logo: "SMALL AGENCIES",
    icon: <Palette />
  },
  {
    name: "Wellness Studio",
    type: "Health & Wellness",
    category: "Wellness Services",
    image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=400&fit=crop",
    logo: "WELLNESS STUDIO",
    icon: <Heart />
  },
  {
    name: "Local Businesses",
    type: "Retail & Services",
    category: "Retail & Hospitality",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=400&fit=crop",
    logo: "LOCAL BUSINESSES",
    icon: <Store />
  }
];

const OurClientsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const clientsRef = useRef(null);
  
  // Handle scroll of client cards
  const handleScroll = () => {
    if (clientsRef.current) {
      const container = clientsRef.current;
      
      // Find which client is most visible
      const clientElements = Array.from(container.querySelectorAll('.client-card'));
      let closestClient = 0;
      let closestDistance = Infinity;
      
      clientElements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const distance = Math.abs((rect.left + rect.width/2) - (containerRect.left + containerRect.width/2));
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestClient = index;
        }
      });
      
      if (closestClient !== activeIndex) {
        setActiveIndex(closestClient);
      }
    }
  };
  
  // Add debounce to scroll handler
  useEffect(() => {
    const container = clientsRef.current;
    let scrollTimer;
    
    const handleScrollDebounced = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        handleScroll();
      }, 100);
    };
    
    if (container) {
      container.addEventListener('scroll', handleScrollDebounced);
      return () => {
        container.removeEventListener('scroll', handleScrollDebounced);
        clearTimeout(scrollTimer);
      };
    }
  }, [activeIndex]);
  
  // Scroll to selected client
  const scrollToClient = (index) => {
    setActiveIndex(index);
    
    if (clientsRef.current) {
      const clientElements = clientsRef.current.querySelectorAll('.client-card');
      if (clientElements && clientElements[index]) {
        clientsRef.current.scrollTo({
          left: clientElements[index].offsetLeft - 16, // Account for container padding
          behavior: 'smooth'
        });
      }
    }
  };
  
  // Navigation controls
  const handlePrev = () => {
    const newIndex = Math.max(0, activeIndex - 1);
    scrollToClient(newIndex);
  };
  
  const handleNext = () => {
    const newIndex = Math.min(clients.length - 1, activeIndex + 1);
    scrollToClient(newIndex);
  };

  return (
    <section id="our-clients" className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-[#c4ffa0]/10">
      <div className="max-w-[1600px] mx-auto px-0 md:px-6">
        <div className="text-center mb-8 md:mb-12 px-4 max-w-2xl mx-auto">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-black/80 mb-6">
            Trusted by
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-[2.125rem] font-semibold text-black tracking-tight leading-snug mb-4">
            Our technology has powered growth for
          </h2>
          {/* <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto">
            From content creators to businesses, kinOS powers diverse industries
          </p> */}
        </div>
        
        {/* Desktop navigation controls */}
        <div className="hidden md:flex justify-end mb-4 gap-2 px-4">
          <button 
            onClick={handlePrev}
            disabled={activeIndex === 0}
            className={`p-2 rounded-full border ${
              activeIndex === 0 
                ? 'border-gray-200 text-gray-300 cursor-not-allowed' 
                : 'border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
            aria-label="Previous client"
          >
            <ChevronLeft size={20} />
          </button>
          <button 
            onClick={handleNext}
            disabled={activeIndex === clients.length - 1}
            className={`p-2 rounded-full border ${
              activeIndex === clients.length - 1 
                ? 'border-gray-200 text-gray-300 cursor-not-allowed' 
                : 'border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
            aria-label="Next client"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        
        {/* Full-width container for mobile */}
        <div className="w-full overflow-hidden">
          {/* Horizontally scrollable client cards */}
          <div 
            ref={clientsRef}
            className="flex overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-6 px-4 md:px-0"
          >
            {clients.map((client, index) => (
              <div 
                key={index}
                className={`client-card snap-center w-[calc(100%-32px)] sm:w-[calc(80%-32px)] md:w-[calc(40%-32px)] lg:w-[calc(25%-32px)] mr-4 md:px-0 flex-shrink-0 transition-all duration-500 ${
                  activeIndex === index ? 'scale-100 opacity-100' : 'scale-95 opacity-85'
                }`}
              >
                {index % 2 === 0 ? (
                  // Persona card - bold title with icon
                  <div className="bg-gradient-to-br from-[#c4ffa0] to-green-500 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 aspect-square flex flex-col overflow-hidden group hover:scale-[1.02]">
                    <div className="p-3 md:p-4 flex flex-col items-center justify-center h-full text-center">
                      <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2 md:p-3 mb-3 md:mb-4 shadow-lg">
                        {React.cloneElement(client.icon, { className: 'w-8 h-8 md:w-10 md:h-10 text-white' })}
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-1">{client.name}</h3>
                      <p className="text-white/80 text-base md:text-lg">{client.type}</p>
                    </div>
                  </div>
                ) : (
                  // Image card with logo tag
                  <div className="bg-white border border-gray-200/50 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 aspect-square flex flex-col overflow-hidden group hover:scale-[1.02]">
                    {/* Client image */}
                    <div className="relative h-full overflow-hidden">
                      <img 
                        src={client.image} 
                        alt={client.name}
                        className="w-full h-full object-cover"
                      />
                     
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        
          {/* Mobile indicator dots */}
          <div className="flex justify-center mt-2 gap-2 md:hidden px-4">
            {clients.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToClient(index)}
                className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${
                  activeIndex === index ? 'bg-black' : 'bg-black/40'
                }`}
                aria-label={`View client ${index + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default OurClientsSection; 